<template>
  <Teleport to="body">
    <transition name="modal-backdrop">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-[9999] overflow-y-auto"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
        @wheel.prevent
        @touchmove.prevent
        @scroll.prevent
      >
        <!-- Background overlay -->
        <div class="fixed inset-0 bg-black/50 transition-opacity" @click="closeModal"></div>

        <!-- Modal panel -->
        <div class="flex min-h-full items-center justify-center p-4 text-center" @click.stop>
          <transition name="modal">
            <div v-if="isOpen" class="relative transform overflow-hidden rounded-lg bg-white text-left border-2 border-black shadow-[4px_4px_0_0_rgba(0,0,0,1)] w-full max-w-lg max-h-[90vh]" @click.stop>
            <div class="overflow-y-auto max-h-[90vh] pr-1">
              <!-- Header -->
              <div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div class="flex items-center justify-between mb-4">
                  <h3 class="text-lg font-semibold text-gray-900" id="modal-title">
                    Session Details
                  </h3>
                  <button
                    @click="closeModal"
                    class="rounded-md bg-white text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <span class="sr-only">Close</span>
                    <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <!-- Session Information -->
                <div v-if="session" class="space-y-6">
                  <!-- Person Information -->
                  <div class="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
              <!-- Handle deactivated users -->
              <div v-if="isUserDeactivated(session)" class="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center">
                <UserCircleIcon class="w-8 h-8 text-gray-500" />
              </div>
              <UserAvatar
                v-else
                :src="getProfileImage(session)"
                :name="getName(session)"
                size="lg"
              />
              <div class="flex-1">
                <h4 class="text-md font-semibold text-gray-900">{{ getName(session) }}</h4>
                <p class="text-sm text-gray-600">{{ getRole(session) }}</p>
              </div>
              <div class="flex items-center gap-2">
                <span
                  class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                  :class="getStatusClasses(session)"
                >
                  {{ getStatusText(session) }}
                </span>
                <span
                  class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                  :class="getTypeClasses(getMode(session))"
                >
                  {{ getMode(session) === "face-to-face" ? "Face-to-Face" : "Online" }}
                </span>
              </div>
            </div>

            <!-- Student Learning Styles (Tutor View Only) -->
            <div v-if="(currentUserType === 'tutor' || currentUserType === 'validator') && studentLearningStyles.length > 0" class="p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
              <div class="flex items-start gap-3">
                <svg class="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div class="flex-1">
                  <h5 class="text-sm font-semibold text-blue-900 mb-2">Student's Learning Styles</h5>
                  <div class="flex flex-wrap gap-2">
                    <span
                      v-for="style in studentLearningStyles"
                      :key="style"
                      class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300"
                    >
                      {{ style }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Session Details Grid -->
            <div class="space-y-4">
              <!-- Subject -->
              <div class="space-y-2">
                <label class="text-sm font-medium text-gray-700">Subject</label>
                <div class="flex items-center gap-2 p-2 bg-gray-50 rounded-md">
                  <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    />
                  </svg>
                  <span class="text-gray-900">{{ session.subject }}</span>
                </div>
              </div>

              <!-- Date and Time -->
              <div class="grid grid-cols-2 gap-4">
                <div class="space-y-2">
                  <label class="text-sm font-medium text-gray-700">Date</label>
                  <div class="flex items-center gap-2 p-2 bg-gray-50 rounded-md">
                    <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <span class="text-gray-900 text-sm">{{ session.date }}</span>
                  </div>
                </div>

                <div class="space-y-2">
                  <label class="text-sm font-medium text-gray-700">Time</label>
                  <div class="flex items-center gap-2 p-2 bg-gray-50 rounded-md">
                    <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span class="text-gray-900 text-sm">{{ session.time }}</span>
                  </div>
                </div>
              </div>

              <!-- Location (if face-to-face) -->
              <div v-if="session.location" class="space-y-2">
                <label class="text-sm font-medium text-gray-700">Location</label>
                <div class="flex items-center gap-2 p-2 bg-gray-50 rounded-md">
                  <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span class="text-gray-900 text-sm break-words" :title="session.location">{{ session.location }}</span>
                </div>
              </div>

              <!-- Notes -->
              <div class="space-y-2 mb-4">
                <label class="text-sm font-medium text-gray-700">Notes</label>
                <div class="p-2 bg-gray-50 rounded-md">
                  <p v-if="(session as any).notes" class="text-gray-900 text-sm">{{ (session as any).notes }}</p>
                  <p v-else class="text-gray-500 text-sm italic">No notes provided</p>
                </div>
              </div>

              <!-- Cancellation Information -->
              <div v-if="(session.status === 'cancelled' || session.status === 'declined') && (session as any).cancellationReason" class="space-y-2 mb-4">
                <label class="text-sm font-medium text-gray-700">Cancellation Details</label>
                <div class="p-3 bg-red-50 border border-red-200 rounded-md">
                  <div class="space-y-2">
                    <div>
                      <span class="text-xs font-medium text-red-600">Reason:</span>
                      <p class="text-red-900 text-sm">{{ formatCancellationReason((session as any).cancellationReason) }}</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Materials -->
              <div class="space-y-2 mb-4">
                <div class="flex items-center justify-between">
                  <label class="text-sm font-medium text-gray-700">Session Materials</label>
                  <!-- Add Materials button (only for pending sessions from student view) -->
                  <button
                    v-if="canEditMaterials"
                    @click="triggerFileUpload"
                    :disabled="isUploading || ((session as any).materials?.length || 0) >= 5"
                    class="px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                    </svg>
                    Add Materials
                  </button>
                </div>

                <!-- Hidden file input -->
                <input
                  ref="fileInput"
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.jpg,.jpeg,.png"
                  @change="handleFileSelect"
                  class="hidden"
                />

                <!-- Uploading indicator -->
                <div v-if="isUploading" class="p-3 bg-blue-50 rounded-md border border-blue-200">
                  <div class="flex items-center gap-2">
                    <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <p class="text-sm text-blue-700">Uploading materials...</p>
                  </div>
                </div>

                <div v-if="(session as any).materials && (session as any).materials.length > 0" class="space-y-2">
                  <div
                    v-for="(material, index) in (session as any).materials"
                    :key="index"
                    class="flex items-center justify-between p-3 bg-gray-50 rounded-md border border-gray-200 hover:bg-gray-100 transition-colors"
                  >
                    <div class="flex items-center gap-2 flex-1 min-w-0">
                      <svg class="w-5 h-5 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                        />
                      </svg>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-gray-900 truncate">{{ material.name }}</p>
                        <p class="text-xs text-gray-500">{{ formatFileSize(material.size) }}</p>
                      </div>
                    </div>
                    <div class="flex items-center gap-2">
                      <button
                        @click="openMaterial(material.url)"
                        class="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 transition-colors flex items-center gap-1 flex-shrink-0"
                      >
                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                        View
                      </button>
                      <!-- Remove button (only for pending sessions from student view) -->
                      <button
                        v-if="canEditMaterials"
                        @click="confirmRemoveMaterial(index, material.name)"
                        :disabled="isUploading"
                        class="px-2 py-1.5 bg-red-600 text-white text-xs font-medium rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Remove material"
                      >
                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
                <div v-else class="p-3 bg-gray-50 rounded-md border border-gray-200">
                  <p class="text-sm text-gray-500 italic">No materials uploaded</p>
                </div>
              </div>
            </div>

            <!-- Ratings Section (only show for completed sessions with feedbacks) -->
            <div v-if="session.status === 'completed' && (session as any).feedbacks && (session as any).feedbacks.length > 0" class="pt-2">
              <div class="flex items-center justify-between mb-3">
                <h4 class="text-md font-semibold text-gray-900">Ratings & Feedback</h4>
                <span
                  class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                  :class="getFeedbackClasses((session as any).feedbackStatus)"
                >
                  {{ (session as any).feedbackStatus === 'given' ? 'Feedback Given' : 'Pending Feedback' }}
                </span>
              </div>
              <div class="grid grid-cols-2 gap-4">
                <!-- Display ratings from feedbacks -->
                <div 
                  v-for="(feedback, index) in (session as any).feedbacks.slice(0, 2)" 
                  :key="index"
                  class="space-y-1"
                >
                  <label class="text-xs font-medium text-gray-700">
                    {{ getFeedbackFromLabel(feedback, session) }}
                  </label>
                  <div class="flex items-center gap-1 p-2 bg-gray-50 border border-gray-200 rounded-md">
                    <StarIcon class="w-4 h-4 text-yellow-500" />
                    <span class="text-gray-900 text-sm font-medium">
                      {{ (feedback.overallScore || 0).toFixed(1) }}/5.0
                    </span>
                  </div>
                </div>
              </div>

        
            </div>

            <!-- Feedback Comments Section (only show for completed sessions with feedback) -->
            <div v-if="session.status === 'completed' && (session as any).feedbacks && (session as any).feedbacks.length > 0" >
              <div class="space-y-3">
                <div 
                  v-for="(feedback, index) in (session as any).feedbacks" 
                  :key="index"
                  class="p-3 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div class="flex items-start justify-between mb-2">
                    <span class="text-sm font-medium text-gray-700">
                      {{ getFeedbackFromLabel(feedback, session) }}
                    </span>
                    <span class="text-xs text-gray-500">
                      {{ formatFeedbackDate(feedback.createdAt || feedback.updatedAt) }}
                    </span>
                  </div>
                  <p v-if="feedback.comment" class="text-sm text-gray-700 leading-relaxed">
                    {{ feedback.comment }}
                  </p>
                  <p v-else class="text-sm text-gray-400 italic">
                    No comment provided
                  </p>
                </div>
              </div>
            </div>

            <!-- Action Buttons (only show for completed sessions) -->
            <div v-if="session.status === 'completed'" class=" pt-4 pb-2">
              <div class="flex gap-2">
                <button
                  v-if="
                    (session as any).feedbackStatus !== 'given' ||
                    !(session as any).yourRating ||
                    (session as any).yourRating === '0'
                  "
                  @click="navigateToFeedback"
                  class="flex-1 px-3 py-2 bg-white border-2 border-orange-700 text-orange-800 rounded-md shadow-[2px_2px_0_0_rgba(194,65,12,1)] hover:bg-orange-500 hover:text-white transition-colors text-xs font-medium flex items-center justify-center gap-1 cursor-pointer"
                >
                  <StarIcon class="w-3 h-3" />
                  Give Feedback
                </button>
                <span
                  v-else
                  class="flex-1 inline-flex items-center justify-center px-3 py-2 rounded-md text-xs font-medium bg-gray-100 text-gray-400 border-2 border-gray-300 cursor-not-allowed"
                >
                  <StarIcon class="w-3 h-3 mr-1" />
                  Feedback Given
                </span>
                <button
                  @click="openMessages"
                  class="flex-1 px-3 py-2 bg-white border-2 border-blue-800 text-blue-800 rounded-md shadow-[2px_2px_0_0_rgba(30,64,175,1)] hover:bg-blue-500 hover:text-white transition-colors text-xs font-medium flex items-center justify-center gap-1 cursor-pointer"
                >
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                  Message
                </button>
              </div>
            </div>
           </div>
            </div>
           </div>
          </div>
         </transition>
       </div>
     </div>
   </transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { StarIcon, UserCircleIcon } from "@heroicons/vue/24/outline";
import type { CompletedSession, UpcomingSession, TutorSession } from "@/types/session";
import { useRouter } from "vue-router";
import UserAvatar from "@/components/UserAvatar.vue";
import { auth, db } from '@/firebase';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { uploadSessionMaterial } from '@/utils/fileUpload';
import Swal from 'sweetalert2';

interface Props {
  isOpen: boolean;
  session: CompletedSession | UpcomingSession | TutorSession | null;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  close: [];
  navigateToFeedback: [session: any];
  openMessages: [session: any];
  materialsUpdated: [];
}>();

const router = useRouter();

// Track current user's type
const currentUserType = ref<string | null>(null);

// Student's learning styles (for tutor view)
const studentLearningStyles = ref<string[]>([]);

// Fetch student's learning styles
const fetchStudentLearningStyles = async (studentId: string) => {
  try {
    const studentDoc = await getDoc(doc(db, 'users', studentId));
    if (studentDoc.exists()) {
      const studentData = studentDoc.data();
      studentLearningStyles.value = studentData.learningStyles || [];
    }
  } catch (error) {
    console.error('Error fetching student learning styles:', error);
    studentLearningStyles.value = [];
  }
};

// Fetch current user's userType from Firestore
const fetchCurrentUserType = async () => {
  if (!auth.currentUser) return null;
  
  try {
    const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
    if (userDoc.exists()) {
      currentUserType.value = userDoc.data().userType || null;
      return currentUserType.value;
    }
  } catch (error) {
    console.error('Error fetching user type:', error);
  }
  return null;
};

// Fetch user type when modal opens
watch(() => props.isOpen, async (isOpen) => {
  if (isOpen) {
    // Fetch user type if not already fetched
    if (!currentUserType.value) {
      await fetchCurrentUserType();
    }
    
    // If tutor/validator view, fetch student's learning styles
    if ((currentUserType.value === 'tutor' || currentUserType.value === 'validator') && props.session) {
      const session = props.session as any;
      if (session.studentId) {
        await fetchStudentLearningStyles(session.studentId);
      }
    }
    
    // Lock body scroll
    document.body.style.overflow = 'hidden';
  } else {
    // Restore body scroll
    document.body.style.overflow = '';
    // Clear learning styles when modal closes
    studentLearningStyles.value = [];
  }
});

// File upload state
const fileInput = ref<HTMLInputElement | null>(null);
const isUploading = ref(false);

// Check if user can edit materials (only for pending sessions from student view)
const canEditMaterials = computed(() => {
  if (!props.session || !auth.currentUser) return false;
  const session = props.session as any;
  return session.status === 'pending' && 'tutorName' in session; // Student view has tutorName
});

const closeModal = () => {
  emit('close');
};

const navigateToFeedback = () => {
  if (props.session) {
    emit('navigateToFeedback', props.session);
    closeModal();
  }
};

const openMessages = () => {
  if (props.session) {
    emit('openMessages', props.session);
    closeModal();
  }
};

const getTypeClasses = (mode: string) => {
  switch (mode) {
    case "face-to-face":
      return "bg-orange-100 text-orange-800 border-2 border-orange-800";
    case "online":
      return "bg-blue-100 text-blue-800 border-2 border-blue-800";
    default:
      return "bg-gray-100 text-gray-800 border-2 border-gray-800";
  }
};

const getFeedbackClasses = (status: string) => {
  switch (status) {
    case "given":
      return "bg-blue-50 border-2 border-blue-600 text-blue-800";
    case "pending":
      return "bg-orange-50 border-2 border-orange-700 text-orange-700";
    default:
      return "bg-gray-50 border-2 border-gray-700 text-gray-700";
  }
};

// Format feedback date
const formatFeedbackDate = (timestamp: any) => {
  if (!timestamp) return '';
  
  try {
    let date: Date;
    
    // Handle Firestore Timestamp
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      date = timestamp.toDate();
    } 
    // Handle Date object
    else if (timestamp instanceof Date) {
      date = timestamp;
    }
    // Handle timestamp number
    else if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    }
    // Handle string
    else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    }
    else {
      return '';
    }
    
    // Format as relative time or absolute date
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined 
      });
    }
  } catch (error) {
    return '';
  }
};

// Determine if feedback is from student or tutor
const getFeedbackFromLabel = (feedback: any, session: any) => {
  const currentUserId = auth.currentUser?.uid;
  
  // Check if current user is the one who gave the feedback
  if (feedback.fromUserID === currentUserId) {
    return 'Your Feedback';
  }
  
  // For student view: if session has tutorId, check if feedback is from tutor
  if ((session as any).tutorId && feedback.fromUserID === (session as any).tutorId) {
    return 'From Tutor';
  }
  
  // For tutor view: if session has studentId, check if feedback is from student
  if ((session as any).studentId && feedback.fromUserID === (session as any).studentId) {
    return 'From Student';
  }
  
  // Fallback
  return 'Feedback';
};

// Helper functions to handle different session types
const getName = (session: any) => {
  // Use currentUserType to determine which name to show
  if (currentUserType.value === 'tutor' || currentUserType.value === 'validator') {
    // Tutor/Validator view - show student's name
    return session.studentName || 'Unknown Student';
  } else {
    // Student view - show tutor's name
    return session.tutorName || 'Unknown Tutor';
  }
};

const getProfileImage = (session: any) => {
  // Use currentUserType to determine which profile image to show
  if (currentUserType.value === 'tutor' || currentUserType.value === 'validator') {
    // Tutor/Validator view - show student's photo
    return session.studentProfileImage || null;
  } else {
    // Student view - show tutor's photo
    return session.tutorProfileImage || null;
  }
};

const getRole = (session: any) => {
  // Use currentUserType to determine which role to show
  if (currentUserType.value === 'tutor' || currentUserType.value === 'validator') {
    // Tutor/Validator view - show "Student"
    return 'Student';
  } else {
    // Student view - show "Tutor"
    return 'Tutor';
  }
};

const getStatusText = (session: any) => {
  if (session.status === 'completed') return 'Completed';
  if (session.status === 'in-progress') return 'In Progress';
  if (session.status === 'confirmed') return 'Confirmed';
  if (session.status === 'pending') return 'Pending';
  if (session.status === 'cancelled') return 'Cancelled';
  if (session.status === 'declined') return 'Declined';
  return 'Unknown';
};

const getStatusClasses = (session: any) => {
  if (session.status === 'completed') return 'bg-green-100 border-2 border-green-800 text-green-800';
  if (session.status === 'in-progress') return 'bg-blue-100 border-2 border-blue-800 text-blue-800';
  if (session.status === 'confirmed') return 'bg-green-100 border-2 border-green-800 text-green-800';
  if (session.status === 'pending') return 'bg-yellow-100 border-2 border-yellow-800 text-yellow-800';
  if (session.status === 'cancelled') return 'bg-red-100 border-2 border-red-800 text-red-800';
  if (session.status === 'declined') return 'bg-red-100 border-2 border-red-800 text-red-800';
  return 'bg-gray-100 border-2 border-gray-800 text-gray-800';
};

const getMode = (session: any) => {
  return session.mode || session.type || 'online';
};

// Open material in a new tab
const openMaterial = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

// Format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

// Trigger file input click
const triggerFileUpload = () => {
  fileInput.value?.click();
};

// Handle file selection
const handleFileSelect = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  const files = target.files;
  
  if (!files || files.length === 0) return;
  
  const session = props.session as any;
  const currentMaterialsCount = session.materials?.length || 0;
  
  // Check max limit
  if (currentMaterialsCount + files.length > 5) {
    await Swal.fire({
      icon: 'warning',
      title: 'Too Many Files',
      text: `You can only upload up to 5 materials. You currently have ${currentMaterialsCount} material(s).`,
      confirmButtonText: 'OK'
    });
    target.value = ''; // Reset input
    return;
  }
  
  isUploading.value = true;
  
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('User not authenticated');
    
    // Upload files
    const uploadPromises = Array.from(files).map(file => 
      uploadSessionMaterial(file, currentUser.uid, session.id)
    );
    
    const uploadedFiles = await Promise.all(uploadPromises);
    
    // Update session in Firestore
    const sessionRef = doc(db, 'sessions', session.id);
    const updatedMaterials = [...(session.materials || []), ...uploadedFiles];
    
    await updateDoc(sessionRef, {
      materials: updatedMaterials
    });
    
    // Update local session data
    if (session.materials) {
      session.materials.push(...uploadedFiles);
    } else {
      session.materials = uploadedFiles;
    }
    
    await Swal.fire({
      icon: 'success',
      title: 'Materials Uploaded',
      text: `${uploadedFiles.length} material(s) added successfully!`,
      timer: 2000,
      showConfirmButton: false
    });
    
    emit('materialsUpdated');
  } catch (error) {
    console.error('Error uploading materials:', error);
    await Swal.fire({
      icon: 'error',
      title: 'Upload Failed',
      text: error instanceof Error ? error.message : 'Failed to upload materials',
      confirmButtonText: 'OK'
    });
  } finally {
    isUploading.value = false;
    target.value = ''; // Reset input
  }
};

// Confirm and remove material
const confirmRemoveMaterial = async (index: number, fileName: string) => {
  const result = await Swal.fire({
    icon: 'warning',
    title: 'Remove Material?',
    html: `Are you sure you want to remove<br><strong>${fileName}</strong>?`,
    showCancelButton: true,
    confirmButtonText: 'Yes, Remove',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#dc2626',
    cancelButtonColor: '#6b7280'
  });
  
  if (result.isConfirmed) {
    await removeMaterial(index);
  }
};

// Remove material
const removeMaterial = async (index: number) => {
  const session = props.session as any;
  
  try {
    // Remove from array
    const updatedMaterials = [...session.materials];
    updatedMaterials.splice(index, 1);
    
    // Update Firestore
    const sessionRef = doc(db, 'sessions', session.id);
    await updateDoc(sessionRef, {
      materials: updatedMaterials
    });
    
    // Update local data
    session.materials = updatedMaterials;
    
    await Swal.fire({
      icon: 'success',
      title: 'Material Removed',
      text: 'Material removed successfully!',
      timer: 1500,
      showConfirmButton: false
    });
    
    emit('materialsUpdated');
  } catch (error) {
    console.error('Error removing material:', error);
    await Swal.fire({
      icon: 'error',
      title: 'Failed to Remove',
      text: 'Could not remove material. Please try again.',
      confirmButtonText: 'OK'
    });
  }
};

// Helper function to check if user is deactivated
const isUserDeactivated = (session: any): boolean => {
  const userName = getName(session);
  return userName === "Deactivated User";
};

// Helper function to format cancellation reasons
const formatCancellationReason = (reason: string): string => {
  if (!reason) return "";
  
  // Convert kebab-case and snake_case to proper title case
  return reason
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
};
</script>

<style scoped>
/* Modal backdrop fade transition */
.modal-backdrop-enter-active,
.modal-backdrop-leave-active {
  transition: opacity 0.2s ease;
}

.modal-backdrop-enter-from,
.modal-backdrop-leave-to {
  opacity: 0;
}

/* Modal content animations - simple fade */
.modal-enter-active {
  transition: opacity 0.2s ease;
}

.modal-leave-active {
  transition: opacity 0.15s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
